import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { FieldType } from "@ngx-formly/core";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { LoggerService } from "@vp/shared/logger-service";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import { map, tap } from "rxjs/operators";
import {
  AddOpinionFormComponent,
  OverrideFormDialogData
} from "../add-opinion-form/add-opinion-form.component";
import {
  AddServiceFormComponent,
  OverrideServiceFormDialogData
} from "../add-service-form/add-service-form.component";
import {
  EditServiceCostModel,
  EditServiceCostService,
  OverrideModel
} from "../edit-service-cost.service";

@Component({
  selector: "vp-edit-service-cost",
  standalone: false,
  templateUrl: "./edit-service-cost.component.html",
  styleUrls: ["./edit-service-cost.component.scss"],
  providers: [EditServiceCostService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditServiceCostComponent extends FieldType implements OnInit {
  private static readonly NAME = "EditServiceCostComponent";

  constructor(
    @Inject(EditServiceCostService) private readonly storeService: EditServiceCostService,
    private readonly matDialog: MatDialog,
    private readonly sharedConfirmationService: SharedConfirmationService,
    private readonly notificationService: NotificationService,
    private readonly logger: LoggerService
  ) {
    super();
  }

  allCaseTypes$ = this.storeService.allCaseTypes$;
  errorMessages$ = this.storeService.errorMessages$.pipe(filterNullMap());
  feeBasedServicesEnabled$ = this.storeService.feeBasedServicesEnabled$;

  dataSource$ = this.storeService.overrides$.pipe(
    tap(overrides => (this.model.serviceCostOverrides = overrides)),
    map(overrides => {
      let newOverrides: any[] = [];
      overrides.forEach(override => {
        if (
          override.serviceCosts &&
          Array.isArray(override.serviceCosts) &&
          override.serviceCosts?.length > 0
        ) {
          newOverrides = [
            ...newOverrides,
            { ...override, servicesTableData: new MatTableDataSource(override.serviceCosts) }
          ];
        } else {
          newOverrides = [...newOverrides, override];
        }
      });
      const dataSource = new MatTableDataSource(newOverrides);
      return dataSource;
    })
  );

  caseTypeLookup$ = this.storeService.caseTypeLookup$;
  feeBasedServiceLookup$ = this.storeService.feeBasedServiceLookup$;

  ngOnInit(): void {
    this.storeService.setOverrides(this.model.serviceCostOverrides);
  }

  addHandler(event: any) {
    event.preventDefault();
    this.matDialog
      .open(AddOpinionFormComponent, {
        disableClose: true,
        data: {
          allCaseTypes: this.storeService.filteredCaseTypes$(
            this.model.serviceCostOverrides ? this.model.serviceCostOverrides : []
          ),
          override: new OverrideModel(),
          mode: "create"
        } as OverrideFormDialogData,
        panelClass: "mat-mdc-dialog-basic"
      } as MatDialogConfig)
      .afterClosed()
      .subscribe({
        next: (newOverride: OverrideModel): void => {
          const result = this.storeService.addOverride(newOverride);
          if (result.errors) {
            this.notificationService.errorMessage("Cost override already exists.");
          }
        },
        error: error => {
          this.logger.errorEvent(
            error,
            `${EditServiceCostComponent.NAME}.${this.addHandler.name}.matDialog.afterClosed`
          );
        }
      });
  }

  addServiceCostOveride(override: OverrideModel) {
    this.matDialog
      .open(AddServiceFormComponent, {
        disableClose: true,
        data: {
          feeBasedServices: this.storeService.filteredFeebasedServices$(
            override?.serviceCosts ?? []
          ),
          override: { friendlyId: undefined, active: true, fee: undefined },
          mode: "create"
        } as OverrideServiceFormDialogData
      } as MatDialogConfig)
      .afterClosed()
      .subscribe({
        next: (newServiceOverride: EditServiceCostModel) => {
          if (newServiceOverride) {
            const updatedOverride = new OverrideModel(
              override.caseTypeId,
              override.serviceCosts ? override.serviceCosts : []
            );
            updatedOverride.serviceCosts.push(newServiceOverride);
            const result = this.storeService.updateOverride(updatedOverride);
            if (result.errors) {
              this.notificationService.errorMessage("Service cost override cannot be updated.");
            }
          }
        },
        error: error => {
          this.logger.errorEvent(
            error,
            `${EditServiceCostComponent.NAME}.${this.addServiceCostOveride.name}.matDialog.afterClosed`
          );
        }
      });
  }

  deleteHandler(updatedOverride: OverrideModel) {
    this.sharedConfirmationService
      .open(`Delete all opinion service cost overrides?`)
      .afterConfirmed()
      .subscribe({
        next: () => {
          const result = this.storeService.deleteOverride(updatedOverride);
          if (result.errors) {
            this.notificationService.errorMessage("Cost override cannot be deleted.");
          }
        },
        error: error => {
          this.logger.errorEvent(
            error,
            `${EditServiceCostComponent.NAME}.${this.deleteHandler.name}.sharedConfirmationService.afterConfirmed`
          );
        }
      });
  }

  updateFeeBasedServiceHandler(
    editOverride: OverrideModel,
    feeBasedServices: EditServiceCostModel
  ) {
    this.matDialog
      .open(AddServiceFormComponent, {
        disableClose: true,
        data: {
          feeBasedServices: this.storeService.filteredFeebasedServices$([]),
          override: feeBasedServices,
          mode: "edit"
        } as OverrideServiceFormDialogData
      } as MatDialogConfig)
      .afterClosed()
      .subscribe({
        next: (updatedServiceOverride: EditServiceCostModel) => {
          if (updatedServiceOverride) {
            const updatedOverride = new OverrideModel(
              editOverride.caseTypeId,
              editOverride.serviceCosts
            );
            const serviceIndex = this.getServiceIndex(
              updatedOverride.serviceCosts,
              updatedServiceOverride
            );
            updatedOverride.serviceCosts.splice(serviceIndex, 1, updatedServiceOverride);
            const result = this.storeService.updateOverride(updatedOverride);
            if (result.errors) {
              this.notificationService.errorMessage("Service cost override cannot be updated.");
            }
          }
        },
        error: error => {
          this.logger.errorEvent(
            error,
            `${EditServiceCostComponent.NAME}.${this.updateFeeBasedServiceHandler.name}.matDialog.afterClosed`
          );
        }
      });
  }

  deleteFeeBasedServiceHandler(
    editOverride: OverrideModel,
    feeBasedServices: EditServiceCostModel
  ) {
    this.sharedConfirmationService
      .open(`Delete service cost override for fee $${feeBasedServices?.fee}?`)
      .afterConfirmed()
      .subscribe({
        next: () => {
          const updatedOverride = new OverrideModel(
            editOverride.caseTypeId,
            editOverride.serviceCosts
          );
          const result = this.storeService.deleteServiceOverride(updatedOverride, feeBasedServices);
          if (result.errors) {
            this.notificationService.errorMessage("Service cost override cannot be deleted.");
          }
        },
        error: error => {
          this.logger.errorEvent(
            error,
            `${EditServiceCostComponent.NAME}.${this.deleteFeeBasedServiceHandler.name}.sharedConfirmationService.afterConfirmed`
          );
        }
      });
  }

  private getServiceIndex(
    services: EditServiceCostModel[],
    findService: EditServiceCostModel
  ): number {
    return services.findIndex(
      (serviceOverride: EditServiceCostModel) =>
        serviceOverride?.friendlyId === findService?.friendlyId
    );
  }
}
